import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DialogCheckboxDescriptor } from "../../../../components/UIToolkit/Popups/Dialog";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { Nurse } from "../../../../utils/models";

/**
 * Stable, never-translated identities for the deactivation checkboxes. Callers match on these in
 * `SystemDialog`'s `onConfirm` payload — the labels are translated and interpolated with the
 * employee's name, so they cannot double as keys.
 */
export const DEACTIVATE_POST_SHIFTS_CHECKBOX_ID = "deactivate.postShifts";
export const DEACTIVATE_FUTURE_SHIFTS_ACK_ID = "deactivate.ack.futureShifts";
export const DEACTIVATE_RECURRING_ACK_ID = "deactivate.ack.recurringSchedule";

/**
 * "Post the freed shifts" starts unticked for both a single employee and a batch: schedulers opt in
 * to reposting a deactivated nurse's deleted shifts rather than having it happen automatically. The
 * two live here together because the difference is a decision, not a drift between two files.
 *
 * Unticked matters most for a batch, where a single tick can post several hundred open shifts and
 * notify every eligible nurse about each of them — a scale at which "I didn't realise it would do
 * that" is unrecoverable. Anything reading one of these must read the other, so changing one alone
 * can't quietly diverge.
 */
export const DEACTIVATE_POST_SHIFTS_DEFAULT_SINGLE = false;
export const DEACTIVATE_POST_SHIFTS_DEFAULT_BULK = false;

const useNurseFeatureData = (nurse: Nurse | undefined) => {
	const { t } = useTranslation();
	const { isNurseInFutureShifts, isNurseInFutureRecurringShifts, chosenDepartment } = useHospitalData();
	const deptDeactivationDateStr = nurse?.departmentScheduledDeactivationDates?.[chosenDepartment.department_id] ?? null;
	const [deactivationDate, setDeactivationDate] = useState<string>(deptDeactivationDateStr ?? "");

	const deactivateNursesCheckboxes: DialogCheckboxDescriptor[] = useMemo(() => {
		const checkboxes: DialogCheckboxDescriptor[] = [];
		if (!nurse) {
			return checkboxes;
		}
		if (isNurseInFutureShifts(nurse.id, deactivationDate)) {
			checkboxes.push({
				id: DEACTIVATE_FUTURE_SHIFTS_ACK_ID,
				label: t("team.deactivate.ack.future_shifts", { name: nurse.firstName }),
				required: true,
				defaultValue: false,
			});
		}

		if (isNurseInFutureRecurringShifts(nurse.id, deactivationDate)) {
			checkboxes.push({
				id: DEACTIVATE_RECURRING_ACK_ID,
				label: t("team.deactivate.ack.recurring_schedule", { name: nurse.firstName }),
				required: true,
				defaultValue: false,
			});
		}

		checkboxes.push({
			id: DEACTIVATE_POST_SHIFTS_CHECKBOX_ID,
			label: t("team.deactivate.post_shifts.label"),
			helperText: t("team.deactivate.post_shifts.helper"),
			required: false,
			defaultValue: DEACTIVATE_POST_SHIFTS_DEFAULT_SINGLE,
		});

		return checkboxes;
	}, [nurse, isNurseInFutureShifts, isNurseInFutureRecurringShifts, deactivationDate, t]);

	return {
		deactivateNursesCheckboxes,
		deactivationDate,
		setDeactivationDate,
	};
};

export default useNurseFeatureData;
